import { FC } from 'react';
import { Box, Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  SquareCheckedIcon,
  SquareUncheckedIcon,
} from 'src/components/multi_select_checkbox_group';

const ValidationBox = styled(Box)({
  minHeight: '20px',
  marginTop: 0,
});

export interface CheckboxInputProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  error?: boolean;
  errorText?: string;
  required?: boolean;
  'data-testid'?: string;
  /**
   * `start` top-aligns the checkbox with multi-line labels (default).
   * `center` vertically centers the checkbox against the full label block.
   */
  labelVerticalAlign?: 'start' | 'center';
}

const squareCheckboxControl = (
  checked: boolean,
  onChange: (checked: boolean) => void,
  sx?: object,
) => (
  <Checkbox
    icon={<SquareUncheckedIcon />}
    checkedIcon={<SquareCheckedIcon />}
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
    sx={sx}
  />
);

/**
 * Custom checkbox input component with square styling and validation support.
 * Prevents layout shift by reserving space for error messages.
 */
export const CheckboxInput: FC<CheckboxInputProps> = ({
  checked,
  onChange,
  label,
  error = false,
  errorText,
  'data-testid': dataTestId,
  labelVerticalAlign = 'start',
}) => {
  const validationIndent = labelVerticalAlign === 'center' ? 4 : 4.5;

  return (
    <Box data-testid={dataTestId}>
      {labelVerticalAlign === 'center' ? (
        <Box
          component='label'
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            typography: 'body1',
          }}
        >
          {squareCheckboxControl(checked, onChange, { p: 0, flexShrink: 0 })}
          <Box component='span'>{label}</Box>
        </Box>
      ) : (
        <FormControlLabel
          control={squareCheckboxControl(checked, onChange)}
          label={label}
          sx={{
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': {
              mt: 0.5,
            },
          }}
        />
      )}
      <ValidationBox sx={{ ml: validationIndent }}>
        <FormHelperText error={error}>
          {error && errorText ? errorText : ' '}
        </FormHelperText>
      </ValidationBox>
    </Box>
  );
};
